/**
 * Active cross-filter chips (S11) — rendered under the parameter bar only
 * while filters are active. Each chip clears its field; the counter is the
 * honest participation count ("filters N of M tiles" — tiles with a binding
 * AND a place for the predicate).
 */
import { X } from 'lucide-react';
import { getEngineForConnection } from '../../../../connections/utils/connectionLookup';
import type { Dashboard } from '../../types/dashboardTypes';
import { crossFilterParticipation } from '../../services/crossFilter';
import {
  selectActiveFilters,
  useDashboardCrossFilterStore,
} from '../../store/dashboardCrossFilterStore';

export function CrossFilterChips({ dashboard }: { dashboard: Dashboard }) {
  const filters = useDashboardCrossFilterStore(selectActiveFilters(dashboard.id));
  const clearField = useDashboardCrossFilterStore((s) => s.clearField);
  const clearAll = useDashboardCrossFilterStore((s) => s.clearAll);
  if (filters.length === 0) return null;

  const { filtered, total } = crossFilterParticipation(dashboard.tiles, filters, (t) =>
    getEngineForConnection(t.connectionId),
  );

  return (
    <div
      className="flex flex-wrap items-center gap-1.5 border-b border-border bg-muted/30 px-4 py-1.5"
      data-testid="dashboard-crossfilter-bar"
    >
      {filters.map((filter) => (
        <span
          key={filter.dashboardField}
          className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-xs"
          data-testid={`crossfilter-chip-${filter.dashboardField}`}
        >
          <span className="font-medium">{filter.dashboardField}</span>
          <span className="text-muted-foreground">
            {filter.value === null ? 'is null' : `= ${String(filter.value)}`}
          </span>
          <button
            onClick={() => clearField(dashboard.id, filter.dashboardField)}
            className="rounded-full p-0.5 hover:bg-primary/20"
            aria-label={`Clear ${filter.dashboardField} filter`}
            data-testid={`crossfilter-chip-clear-${filter.dashboardField}`}
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <span className="text-xs text-muted-foreground" data-testid="crossfilter-count">
        filters {filtered} of {total} tiles
      </span>
      {filters.length > 1 && (
        <button
          onClick={() => clearAll(dashboard.id)}
          className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          data-testid="crossfilter-clear-all"
        >
          Clear all
        </button>
      )}
    </div>
  );
}
